import { axios, getAxiosConfig, ServerAxiosRequestData } from '@config/axios';
import Environment from '@config/index';
import { Product } from '@interfaces/models/product';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { ApiResponse } from '@interfaces/api';
import { ProductConditionLevel } from '@interfaces/models/productConditionLevel';
import { Translation } from '@interfaces/models/translation';
import { Shipping } from '@interfaces/models/shipping';
import { Comment } from '@interfaces/models/comment';
import { NegotiationRequest } from '@interfaces/models/negotiation/negotiationRequest';
import { CreateProductCommentPayload } from '@interfaces/api/payloads/create-product-comment-payload';
import { NegotiationInitialData } from '@interfaces/models/negotiation/negotiationInitialData';
import { User } from '@interfaces/models/user';
import { SellerStats } from '@interfaces/models/seller-info/seller-stats';
import { isNumberId } from '@helpers/utils/general';
import { NegotiationDTO, NegotiationInitialDataDTO } from '@interfaces/models/negotiation/dtos/NegotiationDTO';

type CreateNegotiationPayload = {
  offerInWholeNumber: NegotiationRequest['offerInWholeNumber'];
};

export default class ProductService {
  static apiBaseUrl = Environment.apiBaseUrl;

  static async getProduct(productId: Product['id'], serverAxiosRequestData?: ServerAxiosRequestData): Promise<Product> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    if (!isNumberId(productId)) {
      throw new Error(`Invalid productId: ${productId}`);
    }
    const safeProductId = encodeURIComponent(productId);

    const {
      data: { data },
    } = await axios.get<ApiResponse<Product>>(`${this.apiBaseUrl}/products/${safeProductId}`, axiosConfig);
    return data;
  }

  static async getProductWithBuyerId(
    productId: Product['id'],
    buyerId: string,
    serverAxiosRequestData: ServerAxiosRequestData,
  ): Promise<Product> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    if (!isNumberId(productId)) {
      throw new Error(`Invalid productId: ${productId}`);
    }
    const safeProductId = encodeURIComponent(productId);
    axiosConfig.params = {
      ...axiosConfig.params,
      buyerId,
    };

    const {
      data: { data },
    } = await axios.get<ApiResponse<Product>>(`${this.apiBaseUrl}/products/${safeProductId}`, axiosConfig);
    return data;
  }

  static async getProductConditionDetails(productId: Product['id']): Promise<ProductConditionLevel[]> {
    const axiosConfig = getAxiosConfig();

    const {
      data: { data },
    } = await axios.get<ApiResponse<ProductConditionLevel[]>>(
      `${this.apiBaseUrl}/products/${productId}/condition-details`,
      axiosConfig,
    );
    return data;
  }

  static async translation(commentId: Comment['id']): Promise<Translation> {
    const axiosConfig = getAxiosConfig();

    // There're other typeId,
    // Ex: 1 is for translate comments, 2 is for product description...
    const translationCommentType = 1;

    const {
      data: { data },
    } = await axios.get<ApiResponse<Translation>>(
      `${this.apiBaseUrl}/translations/${commentId}?typeId=${translationCommentType}`,
      axiosConfig,
    );
    return data;
  }

  static async createProductComment(productId: Product['id'], payload: CreateProductCommentPayload): Promise<Comment> {
    const axiosConfig = getAxiosConfig();
    const {
      data: { data },
    } = await axios.post<ApiResponse<Comment>>(
      `${this.apiBaseUrl}/products/${productId}/comments`,
      payload,
      axiosConfig,
    );
    return data;
  }

  static async listProductComments(
    productId: Product['id'],
    limit: number,
    offset: number,
  ): Promise<ApiResponse<Comment[]>> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params = {
      ...axiosConfig.params,
      limit,
      offset,
    };

    const { data } = await axios.get<ApiResponse<Comment[]>>(
      `${this.apiBaseUrl}/products/${productId}/comments`,
      axiosConfig,
    );

    // This response is kinda whack, if no comments are present on a product, meta is an empty object, but we need a count
    if (!data.meta.hasOwnProperty('count')) {
      data.meta.count = 0;
    }

    return data;
  }

  static async getActiveNegotiation(productId: Product['id'], withEvents: boolean): Promise<Negotiation> {
    const axiosConfig = getAxiosConfig();

    if (withEvents) {
      axiosConfig.params.withEvents = withEvents;
    }

    const {
      data: { data },
    } = await axios.get<ApiResponse<NegotiationDTO>>(`${this.apiBaseUrl}/products/${productId}/active-negotiation`, {
      ...axiosConfig,
      validateStatus: (status) => [200, 404].includes(status),
    });

    if (!data) {
      return null;
    }

    return data;
  }

  static async deletePriceDrop(productId: Product['id']): Promise<void> {
    const axiosConfig = getAxiosConfig();
    if (!isNumberId(productId)) {
      throw new Error(`Invalid productId: ${productId}`);
    }
    await axios.delete<ApiResponse<void>>(`${this.apiBaseUrl}/products/${productId}/price-drop-alerts`, axiosConfig);
  }

  static async likeProduct(productId: Product['id']): Promise<void> {
    const axiosConfig = getAxiosConfig();
    await axios.post<ApiResponse<void>>(`${this.apiBaseUrl}/products/${productId}/likes`, null, axiosConfig);
  }

  static async unlikeProduct(productId: Product['id']): Promise<void> {
    const axiosConfig = getAxiosConfig();
    await axios.delete<ApiResponse<void>>(`${this.apiBaseUrl}/products/${productId}/likes`, axiosConfig);
  }

  static async getProductShippingInformation(
    productId: Product['id'],
    serverAxiosRequestData?: ServerAxiosRequestData,
  ): Promise<Shipping> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    if (!isNumberId(productId)) {
      throw new Error(`Invalid productId: ${productId}`);
    }
    const safeProductId = encodeURIComponent(productId);

    const {
      data: { data },
    } = await axios.get<ApiResponse<Shipping>>(`${this.apiBaseUrl}/products/${safeProductId}/shippings`, axiosConfig);
    return data;
  }

  static async createNegotiation(productId: Product['id'], payload: CreateNegotiationPayload): Promise<Negotiation> {
    const axiosConfig = getAxiosConfig();
    const {
      data: { data },
    } = await axios.post<ApiResponse<NegotiationDTO>>(
      `${this.apiBaseUrl}/products/${productId}/negotiations`,
      payload,
      axiosConfig,
    );
    return data;
  }

  static async getNegotiationInitialData(
    productId: Product['id'],
    buyerId?: User['id'],
  ): Promise<NegotiationInitialData> {
    const axiosConfig = getAxiosConfig();
    if (buyerId) {
      axiosConfig.params.buyer_id = buyerId;
    }
    const {
      data: { data },
    } = await axios.get<ApiResponse<NegotiationInitialDataDTO>>(
      `${this.apiBaseUrl}/products/${productId}/negotiation-initial-conditions-view`,
      {
        ...axiosConfig,
        validateStatus: (status) => [200, 400].includes(status),
      },
    );

    if (!data) {
      return null;
    }

    return data;
  }

  static async getSellerStats(
    productId: Product['id'],
    serverAxiosRequestData?: ServerAxiosRequestData,
  ): Promise<SellerStats> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    axiosConfig.params.product_id = productId;

    const {
      data: { data },
    } = await axios.get<ApiResponse<SellerStats>>(`${this.apiBaseUrl}/seller/stats`, axiosConfig);

    if (!data) {
      return null;
    }

    return data;
  }
}
